import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Star, Package } from 'lucide-react';
import { ProductVariation } from '../data/products';
import { Button } from './ui/button';
import { Badge } from './ui/badge';

interface ProductVariationsProps {
  variations: ProductVariation[];
  currentProductId: string;
  onVariationChange?: (productId: string) => void;
}

interface VariationGroup {
  label: string;
  variations: ProductVariation[];
}

export function ProductVariations({ 
  variations, 
  currentProductId, 
  onVariationChange 
}: ProductVariationsProps) {
  const navigate = useNavigate();
  const [selectedVariations, setSelectedVariations] = useState<{[key: string]: string}>({});

  // Group variations by label
  const variationGroups: VariationGroup[] = variations.reduce((groups, variation) => {
    const existingGroup = groups.find(g => g.label === variation.label);
    if (existingGroup) {
      existingGroup.variations.push(variation);
    } else {
      groups.push({
        label: variation.label,
        variations: [variation]
      });
    }
    return groups;
  }, [] as VariationGroup[]);

  // Sort variations within each group by sortOrder
  variationGroups.forEach(group => {
    group.variations.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
  });

  // Set default selections based on current product and default variations
  useEffect(() => {
    const defaultSelections: {[key: string]: string} = {};
    
    variationGroups.forEach(group => {
      // First, check if current product is one of the variations in this group
      const currentProductVariation = group.variations.find(
        v => v.linkedProductId === currentProductId
      );
      
      if (currentProductVariation) {
        defaultSelections[group.label] = currentProductVariation.linkedProductId;
      } else {
        // Otherwise, find the default variation in this group
        const defaultVariation = group.variations.find(v => v.isDefault);
        if (defaultVariation) {
          defaultSelections[group.label] = defaultVariation.linkedProductId;
        } else if (group.variations.length > 0) {
          // If no default, select the first one
          defaultSelections[group.label] = group.variations[0].linkedProductId;
        }
      }
    });
    
    setSelectedVariations(defaultSelections);
  }, [variations, currentProductId]);

  const handleVariationSelect = (groupLabel: string, productId: string) => {
    const newSelections = {
      ...selectedVariations,
      [groupLabel]: productId
    };
    setSelectedVariations(newSelections);

    // If this is a different product than current, navigate to it
    if (productId !== currentProductId) {
      if (onVariationChange) {
        onVariationChange(productId);
      } else {
        navigate(`/product/${productId}`);
      }
    }
  };

  if (!variations || variations.length === 0) {
    return null;
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="bg-gradient-to-r from-gray-50 to-gray-100/50 px-6 py-4 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
          <Package className="h-5 w-5 mr-2 text-jamaica-green" />
          Available Options
        </h3>
      </div>
      <div className="p-6">
        <div className="space-y-4">
          {variationGroups.map((group) => (
            <div key={group.label} className="space-y-2">
              <h4 className="text-sm font-medium text-gray-700 uppercase tracking-wide">
                {group.label}
              </h4>

              <div className="flex flex-wrap gap-2">
                {group.variations.map((variation) => {
                  const isSelected = selectedVariations[group.label] === variation.linkedProductId;
                  const isCurrentProduct = variation.linkedProductId === currentProductId;
                  
                  return (
                    <Button
                      key={variation.id}
                      variant={isSelected ? "default" : "outline"}
                      size="sm"
                      onClick={() => handleVariationSelect(group.label, variation.linkedProductId)}
                      className={`
                        relative transition-all duration-200
                        ${isSelected 
                          ? 'bg-jamaica-green hover:bg-jamaica-green/90 text-white border-jamaica-green' 
                          : 'border-gray-300 hover:border-jamaica-green/50 hover:bg-jamaica-green/5'
                        }
                        ${isCurrentProduct ? 'ring-2 ring-jamaica-green/30' : ''}
                      `}
                    >
                      <div className="flex items-center gap-1">
                        {variation.isDefault && (
                          <Star className="h-3 w-3" fill="currentColor" />
                        )}
                        <span>{variation.value}</span>
                      </div>
                      
                      {isCurrentProduct && (
                        <Badge 
                          variant="secondary" 
                          className="absolute -top-3 -right-3 text-xs bg-jamaica-yellow text-jamaica-black border-jamaica-yellow px-1.5 py-0.5 scale-75"
                        >
                          Current
                        </Badge>
                      )}
                    </Button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
} 